import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { styled } from 'styled-components';

import { useToastPersistence } from 'hooks/useToastPersistence';
import { useI18n } from 'i18n/hooks/useI18n';
import type { ToastData } from 'types/ToastData';
import {
  trackDismissToast,
  trackDisplayToast,
} from 'utils/analytics/track/trackToast';
import { getStaticImageSrc } from 'utils/getImageSrc';
import { dismissToast, showToast } from 'zustand-stores/toastsStore';

const confettiDefaults: confetti.Options = {
  spread: 70,
  ticks: 150,
  gravity: 0.8,
  decay: 0.96,
  startVelocity: 20,
  origin: { x: 0, y: 1 },
  angle: 60,
  colors: ['68BD52', 'EF444D', 'F6C702', 'F6C702'],
  zIndex: 1000000,
  disableForReducedMotion: true,
};

async function shoot() {
  // TODO: this causes a catch 22: linter conflicts with prettier
  const confetti = (
    await import(
      /* webpackChunkName: "canvas-confetti.[contenthash]" */ 'canvas-confetti'
    )
  ).default;

  confetti({
    ...confettiDefaults,
    particleCount: 40,
    scalar: 1.2,
    shapes: ['star'],
  });

  confetti({
    ...confettiDefaults,
    particleCount: 20,
    scalar: 0.75,
    shapes: ['circle'],
  });
}

const JobAppliedConfettiIcon = styled.span`
  width: 48px;
  aspect-ratio: 625/706;
  --src: url(${getStaticImageSrc('job-applied-toast-non-normalised.webp')});
  background-image: var(--src);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  scale: 1.4;
  translate: 4% 16%;
`;

const TOAST_ID = 'job-applied';
const TOAST_PERSISTENCE_NS = 'job-applied';
export function useJobAppliedConfetti() {
  const { i18n } = useI18n();
  const navigate = useNavigate();

  const { persistDismissedToastId, getDismissal, clear } =
    useToastPersistence(TOAST_PERSISTENCE_NS);

  const checkIfCanDisplay = useCallback(() => {
    const ONE_DAY = 24 * 60 * 60 * 1000;
    const dismissal = getDismissal(TOAST_ID);
    if (!dismissal) return true;

    const age = Date.now() - dismissal.dismissedAt;
    if (age < ONE_DAY) return false;

    clear();
    return true;
  }, [clear, getDismissal]);

  const show = useCallback(
    ({
      currentPageUrl,
      toastData: toastDataOverride,
    }: {
      currentPageUrl: string;
      toastData?: Partial<ToastData>;
    }) => {
      if (!checkIfCanDisplay()) return;

      // Shoot confetti after 300ms delay
      const timeoutId = setTimeout(shoot, 300);

      trackDisplayToast({
        type: 'job_applied',
        current_page_url: currentPageUrl,
      });

      const toastOptions: ToastData = {
        id: TOAST_ID,
        variant: 'success',
        headline: i18n('Well done!'),
        icon: <JobAppliedConfettiIcon />,
        body: (
          <>
            {i18n('Your application has been submitted successfully.')}
            <br />
            {i18n('Fingers crossed!')}
          </>
        ),
        dismissible: true,
        durationMs: 15_000,
        onDismiss: () =>
          trackDismissToast({
            type: 'job_applied',
            current_page_url: currentPageUrl,
          }),
      };
      // Show the toast
      showToast({ ...toastOptions, ...toastDataOverride });
      persistDismissedToastId(TOAST_ID);

      return () => clearTimeout(timeoutId);
    },
    [i18n, checkIfCanDisplay, persistDismissedToastId],
  );

  const showSimple = useCallback(
    (path: string) => {
      const currentPageUrl = `${window.location.origin}${path}`;
      return show({ currentPageUrl });
    },
    [show],
  );

  const showWithRedirect = useCallback(
    (path: string) => {
      const toastData: Partial<ToastData> = {
        actions: [
          {
            label: i18n('View All Applications'),
            onClick: () => {
              dismissToast(TOAST_ID);
              navigate(path);
            },
            type: 'button',
          },
        ],
      };
      const currentPageUrl = `${window.location.origin}${path}`;
      return show({ currentPageUrl, toastData });
    },
    [i18n, navigate, show],
  );
  return { showSimple, showWithRedirect };
}
